/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

/*============================================================
  MATERIAL UI
============================================================*/
import Grid from "@mui/material/Grid2";

/*============================================================
  PROPRIETARY - COMPONENTS
============================================================*/
import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";
import GridDropdown from "../../widgets/GridDropdown";
import GridCheckbox from "../../widgets/GridCheckbox";

/*============================================================
  PROPRIETARY - REDUX DATA SLICES
============================================================*/
import { updateClientInfo } from "../../../states/slcPersonalInformation.js";

/*============================================================
  PROPRIETARY - DATA MODELS
============================================================*/
import { createPersonalInfo } from "./PersonalInfoModel.js";

/*============================================================
  PROPRIETARY - UTILS
============================================================*/
import { reNumbers } from "../../../utils/fieldValidator.js";
import { createPayload, createReduxAction } from "../../../utils/modelHelper.js";
import countryPhoneCode from "../../../data/countryPhoneCodes.json";
import { ConstructionOutlined } from "@mui/icons-material";

export default function SectionResidential() {
  const currApplicant = useSelector((state) => state.app.currApplicant);
  const personalInfo = useSelector((state) => state.personalInformation[currApplicant]);

  const [countryOfResidence, setCountryOfResidence] = useState(personalInfo.countryofresidence);
  const [postcode, setPostcode] = useState(personalInfo.postcode);
  const [blockNumber, setBlockNumber] = useState(personalInfo.blockno);
  const [streetName, setStreetName] = useState(personalInfo.streetname);
  const [unitNumber, setUnitNumber] = useState(personalInfo.unitno);
  const [buildingName, setBuildingName] = useState(personalInfo.buildingname);
  const [isLandedProperty, setIsLandedProperty] = useState(personalInfo.islanded);

  const [countryOfResidenceError, setCountryOfResidenceError] = useState();
  const [postcodeError, setPostcodeError] = useState();
  const [blockNumberError, setBlockNumberError] = useState();
  const [streetNameError, setStreetNameError] = useState();
  const [unitNumberError, setUnitNumberError] = useState();
  const [buildingNameError, setBuildingNameError] = useState();
  const [isLandedPropertyError, setIsLandedPropertyError] = useState();

  const dispatch = useDispatch();

  //REFRESH COMPONENT WITH CURRENT APPLICANTE DATA
  useEffect(() => {
    setCountryOfResidence(personalInfo.countryofresidence);
    setPostcode(personalInfo.postcode);
    setBlockNumber(personalInfo.blockno);
    setStreetName(personalInfo.unitno);
    setUnitNumber(personalInfo.streetname);
    setBuildingName(personalInfo.buildingname);
    setIsLandedProperty(personalInfo.islanded);
  }, [currApplicant]);

  function handleChange(e, object) {
    let id = "";

    let actionType = "";
    let updateAction = null;
    let payload = null;

    const personalInfo = createPersonalInfo();

    if (e instanceof PointerEvent) {
      id = object.props.id;
      if (id === "countryofresidence") {
        personalInfo.countryofresidence = e.target.value;
        setCountryOfResidence(e.target.value);
        actionType = "UPDATE_COUNTRY_OF_RESIDENCE";
      }
    } else {
      id = e.target.id.toLowerCase();

      if (id === "postcode" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) {
          setPostcodeError("Postcode is required.");
        } else if (e.target.value.length > 10) {
          setPostcodeError("Postcode exceeded 10 characters");
        } else setPostcodeError("");

        personalInfo.postcode = e.target.value;
        setPostcode(e.target.value);
        actionType = "UPDATE_POSTCODE";
      }

      if (id === "blocknumber") {
        if (e.target.value.length === 0) {
          setBlockNumberError("Block number is required.");
        } else if (e.target.value.length > 10) {
          setBlockNumberError("Block number exceeded 10 characters");
        } else setBlockNumberError("");

        personalInfo.blockno = e.target.value;
        setBlockNumber(e.target.value);
        actionType = "UPDATE_BLOCK_NO";
      }

      if (id === "streetname") {
        if (e.target.value.length === 0) {
          setStreetNameError("Street name is required.");
        } else if (e.target.value.length > 10) {
          setStreetNameError("Street name exceeded 10 characters");
        } else setStreetNameError("");

        personalInfo.streetname = e.target.value;
        setStreetName(e.target.value);
        actionType = "UPDATE_STREET_NAME";
      }

      if (id === "unitnumber") {
        if (e.target.value.length === 0) {
          setUnitNumberError("Unit number is required.");
        } else if (e.target.value.length > 10) {
          setUnitNumberError("Unit number exceeded 10 characters");
        } else setUnitNumberError("");

        personalInfo.unitno = e.target.value;
        setUnitNumber(e.target.value);
        actionType = "UPDATE_UNIT_NO";
      }

      if (id === "buildingname") {
        if (e.target.value.length === 0) {
          setBuildingNameError("Building name is required.");
        } else if (e.target.value.length > 10) {
          setBuildingNameError("Building name exceeded 10 characters");
        } else setBuildingNameError("");

        personalInfo.buildingname = e.target.value;
        setBuildingName(e.target.value);
        actionType = "UPDATE_BUILDING_NAME";
      }

      if (id === "islandedproperty") {
        personalInfo.islanded = e.target.checked;
        setIsLandedProperty(e.target.checked);
        actionType = "UPDATE_IS_LANDED";
      }
    }

    payload = createPayload(currApplicant, personalInfo);
    updateAction = createReduxAction(actionType, payload);
    dispatch(updateClientInfo(updateAction));
  }

  return (
    <Grid container spacing={2}>
      <GridSectionHeader sectionName="Residential" />

      {/* COUNTRY OF RESIDENCE */}
      <GridDropdown
        id="countryofresidence"
        key="countryofresidence"
        columnSize="6"
        label="Country of Residence"
        defaultValue=""
        value=""
        currState={countryOfResidence}
        onChange={handleChange}
        data={countryPhoneCode}
        dataType="country"
      />

      {/* POSTCODE */}
      <GridTextfield
        id="postcode"
        key="postcode"
        columnSize="6"
        label="Postcode"
        currState={postcode}
        onChange={handleChange}
        inputProps={{ maxLength: 10 }}
      />

      {/* BLOCK NUMBER */}
      <GridTextfield
        id="blocknumber"
        key="blocknumber"
        columnSize="6"
        label="Block Number"
        currState={blockNumber}
        onChange={handleChange}
      />

      {/* STREET NAME */}
      <GridTextfield
        id="streetname"
        key="streetname"
        columnSize="6"
        label="Street Name"
        currState={streetName}
        onChange={handleChange}
      />

      {/* Unit Number */}
      <GridTextfield
        id="unitnumber"
        key="unitnumber"
        columnSize="6"
        label="Unit Number"
        currState={unitNumber}
        onChange={handleChange}
      />

      {/* Building Name - Optional */}
      <GridTextfield
        id="buildingname"
        key="buildingname"
        columnSize="6"
        label="Building Name (Optional)"
        currState={buildingName}
        onChange={handleChange}
        required={false}
      />

      {/* IS LANDED PROPERTY */}
      <GridCheckbox
        id="islandedproperty"
        key="islandedproperty"
        columnSize="6"
        label="Is this a landed property"
        required
        checked={isLandedProperty}
        onChange={handleChange}
      />
    </Grid>
  );
}
